<script setup lang="ts">

import { usePresentLevelsStore } from "@/presentlevels/presentlevels.store.ts";
import { useVisibilityStore } from '@/visibility/visibility.store'
import { computed, onMounted, Ref, ref, watch } from "vue";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import GbButton from "@/components/GbButton.vue";
import { useRouter, useRoute } from "vue-router";
import Dropdown from 'primevue/dropdown'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const presentLevelsStore = usePresentLevelsStore()
const visibilityStore = useVisibilityStore()
const router = useRouter()
const route = useRoute()

const urlRef = computed(() => {
  return route.query.ref
})

const gradeOptions = computed(() => {
  return presentLevelsStore.gradeOptions.map(grade => ({
    label: grade.label,
    value: grade.uuid
  }))
})

const demoSubjectOptions = computed(() => {
  return presentLevelsStore.subjectOptions.filter(subject => subject.label === 'Reading')
})

const selectableSubjectOptions = computed(() => {
  return presentLevelsStore.subjectOptions
})

const nonDemoSubjectOptions = computed(() => {
  return presentLevelsStore.subjectOptions.filter(subject => subject.label !== 'Reading')
})

const selectableSubjectColumns = computed(() => {
  const options = selectableSubjectOptions.value
  if (options.length === 1) {
    return [[options[0]],[]]
  }
  const midpoint = Math.floor(options.length / 2)
  return [
    options.slice(0, midpoint),
    options.slice(midpoint)
  ]
})

const studentName = ref('')

const selectedSubjectsMap: Ref<Record<string, boolean>> = ref({})
const selectedSubjects: Ref<string[]> = computed(() => {
  return Object.keys(selectedSubjectsMap.value).filter(uuid => selectedSubjectsMap.value[uuid])
})

const selectedGrade: Ref<string> = ref('')

const validationErrors: Ref<{
  studentName: boolean
  subjects: boolean
  grade: boolean
}> = ref({
  studentName: false,
  subjects: false,
  grade: false
})

function validateForm(): boolean {
  validationErrors.value = {
    studentName: !studentName.value.trim(),
    subjects: !selectedSubjects.value.length,
    grade: !selectedGrade.value
  }
  return !validationErrors.value.studentName && 
         !validationErrors.value.subjects && 
         !validationErrors.value.grade
}

async function submitForm() {
  if (!validateForm()) {
    return
  }
  
  try {
    await presentLevelsStore.createSession({
      studentName: studentName.value,
      gradeUUID: selectedGrade.value,
      subjectUUIDs: selectedSubjects.value
    })
    analytics.sendEvent(
      EVENT_NAMES.PRESENTLEVELS.NEW_SESSION.SUBMIT,
      {
        plw_session_enrolled_grade: selectedGradeLabel.value,
        plw_session_subjects: selectedSubjectLabels.value,
      }
    )
    await router.push({
      name: "PresentlevelsCurrentSession",
      params: {
        session_uuid: presentLevelsStore.plSession?.uuid
      }
    })
  } catch (e) {
    emit('error', e)
  }
}

const nonDemoSubjectColumns = computed(() => {
  if (!nonDemoSubjectOptions.value.length) {
    return []
  }
  const maxColumnLength = Math.ceil(nonDemoSubjectOptions.value.length/2)
  return [
    nonDemoSubjectOptions.value.slice(0, maxColumnLength),
    nonDemoSubjectOptions.value.slice(maxColumnLength)
  ]
})

const emit = defineEmits(["error"])

const selectedGradeLabel = computed(() => {
  const grade = presentLevelsStore.levelOptions.find(level => level.uuid === selectedGrade.value)
  return grade ? grade.abbrev : ''
})

const selectedSubjectLabels = computed(() => {
  return selectedSubjects.value.map(uuid => {
    const subject = presentLevelsStore.subjectOptions.find(subject => subject.uuid === uuid)
    return subject ? subject.label : ''
  })
})

const analytics = getAnalyticsTracker()

watch(studentName, (newValue) => {
  if (newValue.trim() && validationErrors.value.studentName) {
    validationErrors.value.studentName = false
  }
})

watch(selectedGrade, (newValue) => {
  if (newValue && validationErrors.value.grade) {
    validationErrors.value.grade = false
  }
})

watch(selectedSubjects, (newValue) => {
  if (newValue.length > 0 && validationErrors.value.subjects) {
    validationErrors.value.subjects = false
  }
})

onMounted(async () => {
  await Promise.all([
    await presentLevelsStore.fetchSubjectOptions(),
    await presentLevelsStore.fetchLevelOptions()
  ])
  analytics.sendEvent(
    EVENT_NAMES.PRESENTLEVELS.NEW_SESSION.VIEWED,
    urlRef.value ? {ref: urlRef.value} : undefined
  )
})

</script>

<template>
  <div class="bg-purple-50 pt-8 pb-10 px-12 rounded-2xl shadow-lg mx-auto mt-8 font-default max-w-2xl w-full">
    <form @submit.prevent="submitForm">
      <div class="mb-6">
        <label for="studentName" class="italic text-display-sm text-default block mb-2">I'm developing present levels for</label>
        <InputText
          v-model="studentName"
          id="studentName"
          placeholder="my student's name"
          class="fs-exclude rounded-lg border shadow-sm px-4 py-3 text-xl w-[400px]"
          :class="{ 'border-red-500': validationErrors.studentName }"
          :aria-invalid="validationErrors.studentName"
          :aria-describedby="validationErrors.studentName ? 'studentName-error' : undefined"
        />
        <p v-if="validationErrors.studentName" id="studentName-error" class="text-md mt-1 flex items-center gap-1" style="color: #dc2626;" role="alert">
          <span class="material-icons-round text-base" style="color: #dc2626;">warning_amber</span>
          Student name is required
        </p>
      </div>

      <div class="mb-4 min-h-24">
        <label for="grade" class="italic text-display-sm text-default block mb-2">who is enrolled in</label>

        <LoadingSkeletonRow
          v-if="presentLevelsStore.loadingLevels"
          width="w-[400px]"
          height="h-12"
          border-radius="rounded-sm"
        />
        <Dropdown
          v-else
          :options="gradeOptions"
          :modelValue="selectedGrade"
          @update:modelValue="value => selectedGrade = value"
          optionLabel="label"
          optionValue="value"
          placeholder="select grade level"
          aria-label="select grade level"
          class="rounded-sm border border-gray-600 shadow-sm px-0 py-0 bg-white text-xl w-[400px]"
          :class="{ 'border-red-500': validationErrors.grade }"
          :aria-invalid="validationErrors.grade"
          :aria-describedby="validationErrors.grade ? 'grade-error' : undefined"
        >
          <template #dropdownicon>
            <span class="material-icons-round text-display-sm text-surface-900">arrow_drop_down</span>
          </template>
        </Dropdown>
        <p v-if="validationErrors.grade" id="grade-error" class="text-md mt-1 flex items-center gap-1" style="color: #dc2626;" role="alert">
          <span class="material-icons-round text-base" style="color: #dc2626;">warning_amber</span>
          Enrolled grade level is required
        </p>
      </div>

      <div class="mb-7">
        <span class="italic text-display-sm text-default block mb-2">and in the area(s) of</span>
        <p v-if="validationErrors.subjects" id="subjects-error" class="text-md mb-2 flex items-center gap-1" style="color: #dc2626;" role="alert">
          <span class="material-icons-round text-base" style="color: #dc2626;">warning_amber</span>
          Select at least 1 present level area
        </p>
        <div v-if="presentLevelsStore.loadingSubjects">
          <LoadingSkeletonRow width="w-60" height="h-6" />
          <div class="flex flex-row gap-8 bg-violet-50 rounded-lg pt-2">
            <div
              v-for="i in Array(2)"
              :key="`skeleton-subject-column-${i}`"
              class="flex flex-col gap-2 flex-1">
              <LoadingSkeletonRow
                v-for="j in Array(9)"
                :key="`skeleton-subject-${i}${j}`"
                :possible-widths="['w-1/2', 'w-3/5', 'w-2/3', 'w-3/4', '4/5']"
              />
            </div>
          </div>
        </div>
        <div v-else-if="demoSubjectOptions.length || selectableSubjectOptions.length" class="flex gap-8 mt-2">
          <div 
            v-for="(column, columnIndex) in selectableSubjectColumns" 
            :key="`subject-column-${columnIndex}`"
            class="flex-1"
          >
            <div 
              v-for="subject in column" 
              :key="subject?.uuid"
              class="flex items-center mb-2"
            >
              <Checkbox
                v-model="selectedSubjectsMap[subject?.uuid]"
                :binary="true"
                :inputId="`subject-option-${subject?.uuid}`"
                gbTheme="purple"
              />
              <label :for="`subject-option-${subject?.uuid}`" class="ml-3 text-base cursor-pointer">{{subject?.label}}</label>
            </div>
          </div>
        </div>
      </div>

      <GbButton type="submit" gb-theme="purplePrimary" class="rounded-lg text-xl py-3 shadow-md w-max">
        Develop Present Levels
      </GbButton>
    </form>
  </div>
</template>
